'use strict';

// Animated dot-grid canvas background with mouse interaction.
// Inspired by soulwire's "30,000 Particles": https://codepen.io/soulwire/pen/DdGRYG
// Usage: <canvas class="js-bg-el …" x-data="particleBg"></canvas>
//
// On screens narrower than 64em, or when the user prefers reduced motion,
// the static dot pattern is shown but the animation loop and mouse
// interaction are both disabled.

;( () => {

	document.addEventListener( 'alpine:init', () => {

		Alpine.data( 'particleBg', () => ({

			_ctx        : null,
			_raf        : null,
			_dots       : [],
			_w          : 0,
			_h          : 0,
			_animated   : false,
			_onResize   : null,
			_resizeTimer: null,
			_onMouse    : null,
			_onLeave    : null,
			_mouse      : { x: -9999, y: -9999 },
			_smoothMouse: { x: -9999, y: -9999 },

			spacing     : 26.25,
			dotRadius   : 1.5,
			accentRadius: 3,
			accentEvery : 6,
			mouseRadius : 120,
			mouseAmp    : 60,
			easeK       : 0.28,
			damping     : 0.42,
			mouseEase   : 0.12,

			init() {

				this._ctx      = this.$el.getContext( '2d' );
				this._animated = ! window.matchMedia( '( width < 64em )' ).matches
					&& ! window.matchMedia( '( prefers-reduced-motion: reduce )' ).matches;

				this._onResize = () => {
					clearTimeout( this._resizeTimer );
					this._resizeTimer = setTimeout( () => this._resize(), 150 );
				};

				window.addEventListener( 'resize', this._onResize );

				// Mouse interaction only when animation is active.
				if ( this._animated ) {

					const section = this.$el.closest( 'section' ) || this.$el.parentElement;

					this._onMouse = ( e ) => {
						const rect = this.$el.getBoundingClientRect();
						const mx   = e.clientX - rect.left;
						const my   = e.clientY - rect.top;

						// Teleport the smooth position on first entry to avoid a phantom sweep.
						if ( -9999 === this._smoothMouse.x ) {
							this._smoothMouse = { x: mx, y: my };
						}

						this._mouse = { x: mx, y: my };
					};

					this._onLeave = () => {
						this._mouse       = { x: -9999, y: -9999 };
						this._smoothMouse = { x: -9999, y: -9999 };
					};

					section.addEventListener( 'mousemove', this._onMouse );
					section.addEventListener( 'mouseleave', this._onLeave );

				}

				// Defer first resize to ensure the section has finished laying out.
				requestAnimationFrame( () => this._resize() );

			},

			_resize() {

				// Cancel any in-flight draw loop before resizing so we don't run two.
				if ( this._raf ) {
					cancelAnimationFrame( this._raf );
					this._raf = null;
				}

				// Read dimensions from the parent so we get the section size, not the
				// canvas's own intrinsic size (which may not be computed yet).
				const parent = this.$el.parentElement;

				this._w = parent.offsetWidth;
				this._h = parent.offsetHeight;

				this.$el.width  = this._w;
				this.$el.height = this._h;

				this._buildDots();
				this._draw();

			},

			_buildDots() {

				const { _w, _h, spacing, accentEvery } = this;
				const cols = Math.ceil( _w / spacing ) + 1;
				const rows = Math.ceil( _h / spacing ) + 1;

				this._dots = [];

				for ( let r = 0; r < rows; r++ ) {
					for ( let c = 0; c < cols; c++ ) {
						this._dots.push({
							x     : c * spacing,
							y     : r * spacing,
							c,
							r,
							accent: 0 === c % accentEvery && 0 === r % accentEvery,
							ox    : 0,
							oy    : 0,
							vx    : 0,
							vy    : 0,
						});
					}
				}

			},

			_draw() {

				const { _ctx, _w, _h, _dots, dotRadius, accentRadius, mouseRadius, mouseAmp, easeK, damping } = this;

				if ( this._animated ) {
					// Smoothly track the raw mouse position.
					this._smoothMouse.x += ( this._mouse.x - this._smoothMouse.x ) * this.mouseEase;
					this._smoothMouse.y += ( this._mouse.y - this._smoothMouse.y ) * this.mouseEase;
				}

				const smx = this._smoothMouse.x;
				const smy = this._smoothMouse.y;

				_ctx.clearRect( 0, 0, _w, _h );

				for ( let i = 0; i < _dots.length; i++ ) {

					const dot    = _dots[ i ];
					let targetDx = 0;
					let targetDy = 0;

					// Repel dots away from the smoothed mouse position.
					const mdx   = dot.x - smx;
					const mdy   = dot.y - smy;
					const dist  = Math.max( 1, Math.sqrt( mdx * mdx + mdy * mdy ) );
					const force = Math.max( 0, ( mouseRadius - dist ) / mouseRadius );

					if ( 0 < force ) {
						targetDx += force * ( mdx / dist ) * mouseAmp;
						targetDy += force * ( mdy / dist ) * mouseAmp;
					}

					// Spring each dot's current offset toward the target.
					dot.vx  = ( dot.vx + ( targetDx - dot.ox ) * easeK ) * damping;
					dot.vy  = ( dot.vy + ( targetDy - dot.oy ) * easeK ) * damping;
					dot.ox += dot.vx;
					dot.oy += dot.vy;

					const r = dot.accent ? accentRadius : dotRadius;
					_ctx.fillStyle = dot.accent ? '#024081' : 'rgba(2, 64, 129, 1)';
					_ctx.fillRect( dot.x + dot.ox - r, dot.y + dot.oy - r, r * 2, r * 2 );

				}

				if ( this._animated ) {
					this._raf = requestAnimationFrame( () => this._draw() );
				}

			},

			destroy() {

				if ( this._raf ) {
					cancelAnimationFrame( this._raf );
					this._raf = null;
				}

				if ( this._onResize ) {
					window.removeEventListener( 'resize', this._onResize );
				}

				if ( this._onMouse || this._onLeave ) {

					const section = this.$el.closest( 'section' ) || this.$el.parentElement;

					if ( this._onMouse ) {
						section.removeEventListener( 'mousemove', this._onMouse );
					}

					if ( this._onLeave ) {
						section.removeEventListener( 'mouseleave', this._onLeave );
					}

				}

			},
		}) );
	});
})();
